'use client'

import { useEffect, useRef, useState } from 'react'
import { AIENT_TELEMETRY_ENVIRONMENT, AIENT_SELF_MONITORING_PUBLISHABLE_KEY, GIT_COMMIT_REF, GIT_COMMIT_SHA, NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT, NEXT_PUBLIC_OTEL_SERVICE_NAME } from '@/lib/env'
import type { BrowserSDK, UserContext } from '@aient/otel-browser'
import { resolveBrowserOtlpExporterUrl } from '@/lib/otel/browser-exporter-url'
import { readStoredPreferences, type CookiePreferences } from '@/lib/analytics/consent'
export type { UserContext } from '@aient/otel-browser'

/** Singleton SDK instance for external access (OAuth callbacks, etc.) */
let _sdk: BrowserSDK | null = null

export const OTEL_INSTALLATION_ID_KEY = 'aient.otel.installation_id'
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function resolveInstallationId(currentDocumentId?: string): string | undefined {
  if (typeof window === 'undefined') return undefined

  if (!readStoredPreferences().analytics) {
    try {
      window.localStorage.removeItem(OTEL_INSTALLATION_ID_KEY)
    } catch {
      // Storage is optional; consent still prevents reuse in this document.
    }
    return undefined
  }

  try {
    const existing = window.localStorage.getItem(OTEL_INSTALLATION_ID_KEY)
    if (existing && UUID_PATTERN.test(existing)) return existing
  } catch {
    // Continue with an ephemeral identifier when storage is unavailable.
  }

  if (currentDocumentId && UUID_PATTERN.test(currentDocumentId)) return currentDocumentId

  try {
    const installationId = crypto.randomUUID()
    try {
      window.localStorage.setItem(OTEL_INSTALLATION_ID_KEY, installationId)
    } catch {
      // The generated value remains useful for the current document.
    }
    return installationId
  } catch {
    return undefined
  }
}

/**
 * Update user context from outside React (OAuth redirects, etc.).
 * AuthProvider owns runtime transitions; TelemetryInit's prop is only the SSR seed.
 * 
 * @param userId - User ID or null to clear
 */
export function updateUserId(userId: string | null): void {
  if (userId) _sdk?.identify({ userId })
  else _sdk?.clearUser()
}

interface TelemetryInitProps {
  /** Optional override for authenticated telemetry export. */
  publishableKey?: string | null
  /** User ID for trace correlation. Pass null/undefined to clear. */
  userId?: string | null
  /** Enable debug logging */
  debug?: boolean
}

/**
 * Initializes browser-side OpenTelemetry.
 * 
 * Browser identity is propagated as untrusted telemetry correlation. Authenticated
 * API wrappers overwrite `enduser.id` from the verified server-side session.
 * 
 * @example
 * ```tsx
 * // In your root layout
 * const user = await getCurrentUser()
 * <TelemetryInit userId={user?.id} />
 *
 * // For explicit overrides
 * <TelemetryInit publishableKey={customKey} userId={user?.id} />
 * ```
 */
export function TelemetryInit({ publishableKey, userId, debug }: TelemetryInitProps): null {
  const sdkRef = useRef<BrowserSDK | null>(null)
  const generationRef = useRef(0)
  const initialUserIdRef = useRef(userId)
  const reconfigurationUserRef = useRef<UserContext | null | undefined>(undefined)
  const installationIdRef = useRef<string | undefined>(undefined)
  const analyticsConsentRef = useRef<boolean | undefined>(undefined)
  const [consentRevision, setConsentRevision] = useState(0)

  useEffect(() => {
    const onConsentChange = (event: Event): void => {
      const preferences = (event as CustomEvent<CookiePreferences>).detail
      if (!preferences || typeof preferences.analytics !== 'boolean') return
      if (analyticsConsentRef.current === preferences.analytics) return
      analyticsConsentRef.current = preferences.analytics
      setConsentRevision(revision => revision + 1)
    }
    window.addEventListener('aientConsentChange', onConsentChange)
    return () => window.removeEventListener('aientConsentChange', onConsentChange)
  }, [])

  // Aient app telemetry is self-monitoring and should default to the app's
  // own publishable key, independent of the currently selected org.
  // Callers may still override the key explicitly for isolated flows.
  useEffect(() => {
    const exporterUrl = resolveBrowserOtlpExporterUrl(
      NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT,
      typeof window !== 'undefined' ? window.location.origin : undefined,
    )
    const effectivePublishableKey =
      publishableKey === undefined ? AIENT_SELF_MONITORING_PUBLISHABLE_KEY : publishableKey

    const generation = ++generationRef.current
    const previous = sdkRef.current
    if (previous) {
      reconfigurationUserRef.current = previous.getUserContext()
      sdkRef.current = null
      if (_sdk === previous) _sdk = null
      void previous.shutdown().catch(() => undefined)
    }

    if (!effectivePublishableKey) return

    // The SDK (with zone.js) loads as its own chunk after hydration instead of in every page's first burst.
    let cancelled = false
    let sdk: BrowserSDK | null = null
    void import('@aient/otel-browser').catch(() => null).then((otel) => {
      if (!otel || cancelled || generationRef.current !== generation) return
      analyticsConsentRef.current = readStoredPreferences().analytics
      installationIdRef.current = resolveInstallationId(installationIdRef.current)
      sdk = otel.registerOTelBrowser({
        serviceName: NEXT_PUBLIC_OTEL_SERVICE_NAME,
        exporterUrl,
        publishableKey: effectivePublishableKey,
        installationId: installationIdRef.current,
        user: reconfigurationUserRef.current === undefined
          ? initialUserIdRef.current
            ? { userId: initialUserIdRef.current }
            : undefined
          : reconfigurationUserRef.current ?? undefined,
        release: {
          commit: GIT_COMMIT_SHA,
          branch: GIT_COMMIT_REF,
          environment: AIENT_TELEMETRY_ENVIRONMENT,
        },
        includeUserAgent: true,
        captureConsoleLogs: true,
        logLevel: debug ? 'DEBUG' : undefined,
      })
      sdkRef.current = sdk
      _sdk = sdk
    })

    return () => {
      cancelled = true
      if (!sdk || generationRef.current !== generation || sdkRef.current !== sdk) return
      reconfigurationUserRef.current = sdk.getUserContext()
      sdkRef.current = null
      if (_sdk === sdk) _sdk = null
      void sdk.shutdown().catch(() => undefined)
    }
  }, [consentRevision, debug, publishableKey])

  return null
}

// ============================================================================
// Re-exports
// ============================================================================

export type { BrowserSDK } from '@aient/otel-browser'
